// design/SsoConfig.dc.html + design/SsoTest.dc.html. sso.rs: GET/PUT/DELETE /orgs/{org_id}/sso,
// GET/POST /orgs/{org_id}/sso/breakglass[/{user_id}], POST /orgs/{org_id}/sso/test,
// GET /orgs/{org_id}/sso/test/{state}.
import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Badge, Card, IconButton, Select, ToggleRow } from "@grackle/ui";
import { SettingsSection } from "./SettingsSection";
import ConfirmDialog from "../components/ConfirmDialog";
import { api, ApiFail } from "../api/client";
import type {
  BreakglassUserOut,
  ListResponse,
  MemberOut,
  OrgDomainOut,
  OrgRole,
  PutSsoConfigRequest,
  SsoConfigOut,
  StartTestResp,
  TestResultOut,
} from "../api/types";
import { Button, Field, Notice } from "../components/ui";
import type { AdminOutletContext } from "./AdminLayout";
import { verifiedDomains } from "../lib/sso";
import { ScimSection } from "./ScimSection";

function Toggle({ on, onToggle, label, sub }: { on: boolean; onToggle: () => void; label: string; sub: string }) {
  return <ToggleRow label={label} description={sub} checked={on} onChange={() => onToggle()} divided />;
}

type RoleMappingRow = { claimValue: string; role: OrgRole };

function mappingToRows(mapping: Record<string, OrgRole>): RoleMappingRow[] {
  return Object.entries(mapping).map(([claimValue, role]) => ({ claimValue, role }));
}
function rowsToMapping(rows: RoleMappingRow[]): Record<string, OrgRole> {
  const out: Record<string, OrgRole> = {};
  for (const row of rows) {
    if (row.claimValue.trim()) out[row.claimValue.trim()] = row.role;
  }
  return out;
}

export default function SsoSettingsPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [config, setConfig] = useState<SsoConfigOut | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Form state, seeded from `config` once loaded.
  const [issuerUrl, setIssuerUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [emailClaim, setEmailClaim] = useState("email");
  const [nameClaim, setNameClaim] = useState("name");
  const [roleClaim, setRoleClaim] = useState("");
  const [roleMappingRows, setRoleMappingRows] = useState<RoleMappingRow[]>([]);
  const [defaultRole, setDefaultRole] = useState<OrgRole>("member");
  const [groupClaim, setGroupClaim] = useState("");
  const [enforced, setEnforced] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const load = useCallback(() => {
    api<SsoConfigOut>("GET", `/orgs/${org.id}/sso`)
      .then((cfg) => {
        setConfig(cfg);
        setIssuerUrl(cfg.issuer_url ?? "");
        setClientId(cfg.client_id ?? "");
        setEmailClaim(cfg.email_claim);
        setNameClaim(cfg.name_claim);
        setRoleClaim(cfg.role_claim ?? "");
        setRoleMappingRows(mappingToRows(cfg.role_mapping));
        setDefaultRole(cfg.default_role);
        setGroupClaim(cfg.group_claim ?? "");
        setEnforced(cfg.enforced);
      })
      .catch((err) => setLoadError(err instanceof ApiFail ? err.message : "Couldn't load SSO settings."));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    const body: PutSsoConfigRequest = {
      issuer_url: issuerUrl.trim(),
      client_id: clientId.trim(),
      client_secret: clientSecret.trim() ? clientSecret.trim() : undefined,
      email_claim: emailClaim.trim() || "email",
      name_claim: nameClaim.trim() || "name",
      role_claim: roleClaim.trim() || null,
      role_mapping: rowsToMapping(roleMappingRows),
      default_role: defaultRole,
      group_claim: groupClaim.trim() || null,
      enforced,
    };
    try {
      const cfg = await api<SsoConfigOut>("PUT", `/orgs/${org.id}/sso`, body);
      setConfig(cfg);
      setClientSecret("");
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof ApiFail ? err.message : "Couldn't save these settings.");
    } finally {
      setSaving(false);
    }
  }

  async function onRemoveConfirm() {
    setRemoveDialogOpen(false);
    setRemoving(true);
    try {
      await api("DELETE", `/orgs/${org.id}/sso`);
      setClientSecret("");
      load();
    } catch (err) {
      setSaveError(err instanceof ApiFail ? err.message : "Couldn't turn off SSO.");
    } finally {
      setRemoving(false);
    }
  }

  function onRemove() {
    setRemoveDialogOpen(true);
  }

  if (loadError && !config) {
    return (
      <div>
        <header className="page-head">
          <div>
            <h1>Single sign-on</h1>
            <p>OIDC only — connect an identity provider (Keycloak, Okta, Entra ID, Google Workspace) so everyone in {org.name} signs in and joins through it.</p>
          </div>
        </header>
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <div className="admin-row-sub" style={{ marginBottom: 12 }}>Couldn't load SSO settings.</div>
          <Button variant="secondary" onClick={load} disabled={saving} loading={saving}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!config) return <div className="admin-row-sub">Loading…</div>;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Single sign-on</h1>
          <p>OIDC only — connect an identity provider (Keycloak, Okta, Entra ID, Google Workspace) so everyone in {org.name} signs in and joins through it.</p>
        </div>
      </header>

      <SettingsSection title="Connection" description="The OIDC issuer and client Grackle signs in through.">
        <div className="admin-two-col">
          <Field
            label="Issuer URL"
            placeholder="https://idp.example.com/realms/acme"
            value={issuerUrl}
            onChange={(e) => setIssuerUrl(e.target.value)}
          />
          <Field label="Client ID" value={clientId} onChange={(e) => setClientId(e.target.value)} />
        </div>
        <Field
          label="Client secret"
          type="password"
          placeholder={config.configured ? "Unchanged — leave blank to keep it" : ""}
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          help={`Redirect URI to register with your IdP: ${config.redirect_uri}`}
        />
      </SettingsSection>

      <SettingsSection title="Attribute mapping" description="Which claims become a member's email, name, role and teams.">
        <div className="admin-two-col">
          <Field label="Email claim" value={emailClaim} onChange={(e) => setEmailClaim(e.target.value)} />
          <Field label="Name claim" value={nameClaim} onChange={(e) => setNameClaim(e.target.value)} />
        </div>
        <div className="admin-two-col">
          <Field
            label="Role claim (optional)"
            placeholder="roles"
            value={roleClaim}
            onChange={(e) => setRoleClaim(e.target.value)}
          />
          <Field
            label="Group claim (optional)"
            placeholder="groups"
            value={groupClaim}
            onChange={(e) => setGroupClaim(e.target.value)}
            help="Each value becomes an IdP-managed team"
          />
        </div>

        {roleClaim.trim() ? (
          <div className="admin-stack">
            <div className="admin-row-sub">Map values of “{roleClaim.trim()}” to a Grackle role</div>
            {roleMappingRows.map((row, i) => (
              <div className="admin-kv-row" key={i}>
                <Field
                  label=""
                  placeholder="claim value, e.g. grackle-admins"
                  value={row.claimValue}
                  onChange={(e) =>
                    setRoleMappingRows((rows) => rows.map((r, j) => (j === i ? { ...r, claimValue: e.target.value } : r)))
                  }
                />
                <Select
                  size="sm"
                  aria-label="Role for this claim value"
                  value={row.role}
                  onChange={(v) =>
                    setRoleMappingRows((rows) =>
                      rows.map((r, j) => (j === i ? { ...r, role: v as OrgRole } : r)),
                    )
                  }
                  options={[
                    { value: "owner", label: "Owner" },
                    { value: "admin", label: "Admin" },
                    { value: "member", label: "Member" },
                  ]}
                />
                <IconButton icon="trash" label="Remove mapping" size="sm" variant="muted" onClick={() => setRoleMappingRows((rows) => rows.filter((_, j) => j !== i))} />
              </div>
            ))}
            <div>
              <Button
                variant="secondary"
                size="sm"
                icon="plus"
                onClick={() => setRoleMappingRows((rows) => [...rows, { claimValue: "", role: "member" }])}
              >
                Add mapping
              </Button>
            </div>
          </div>
        ) : null}
      </SettingsSection>

      <SettingsSection title="Access" description="What a new SSO sign-in becomes, and whether SSO is the only way in.">
        <Select
          className="admin-field--narrow"
          label="Default role"
          value={defaultRole}
          onChange={(v) => setDefaultRole(v as OrgRole)}
          options={[
            { value: "admin", label: "Admin" },
            { value: "member", label: "Member" },
          ]}
        />

        <Toggle
          on={enforced}
          onToggle={() => setEnforced((v) => !v)}
          label="Require SSO for this org"
          sub="Blocks password and Google/Microsoft sign-in for every member of this org, and for anyone at a verified domain below, except the break-glass list."
        />
        <DomainEnforcementSection orgId={org.id} orgName={org.name} enforced={enforced} savedEnforced={config.enforced} />
      </SettingsSection>

      {saveError ? <Notice tone="error">{saveError}</Notice> : null}
      <div className="admin-save-row">
        <Button onClick={onSave} loading={saving} disabled={!issuerUrl.trim() || !clientId.trim()}>
          Save changes
        </Button>
        <Button variant="ghost" onClick={load} disabled={saving}>
          Cancel
        </Button>
        {config.configured ? (
          <Button variant="secondary" onClick={onRemove} loading={removing}>
            Turn off SSO
          </Button>
        ) : null}
        {saved ? <span className="admin-save-row__status">Saved.</span> : null}
      </div>

      {config.configured && enforced ? <BreakglassSection orgId={org.id} /> : null}
      {config.configured ? (
        <TestConnectionSection orgId={org.id} testState={searchParams.get("test_state")} onClearState={() => {
          searchParams.delete("test_state");
          setSearchParams(searchParams, { replace: true });
        }} />
      ) : null}

      <ScimSection orgId={org.id} />

      {/* Issue #1: replace window.confirm */}
      <ConfirmDialog
        open={removeDialogOpen}
        title="Turn off SSO"
        message={`Turn off SSO for ${org.name}? Members will sign in with email and password again.`}
        confirmLabel="Turn off SSO"
        danger
        onClose={() => { setRemoveDialogOpen(false); setRemoving(false); }}
        onConfirm={onRemoveConfirm}
      />
    </>
  );
}

function BreakglassSection({ orgId }: { orgId: string }) {
  const [admins, setAdmins] = useState<BreakglassUserOut[]>([]);
  const [members, setMembers] = useState<MemberOut[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [addUserId, setAddUserId] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api<ListResponse<BreakglassUserOut>>("GET", `/orgs/${orgId}/sso/breakglass`),
      api<ListResponse<MemberOut>>("GET", `/orgs/${orgId}/members`),
    ])
      .then(([bg, m]) => {
        setAdmins(bg.items);
        setMembers(m.items);
      })
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load the break-glass list."));
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  async function onAdd() {
    if (!addUserId) return;
    setAdding(true);
    try {
      await api("POST", `/orgs/${orgId}/sso/breakglass`, { user_id: addUserId });
      setAddUserId("");
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't add that admin.");
    } finally {
      setAdding(false);
    }
  }

  async function onRemove(userId: string) {
    try {
      await api("DELETE", `/orgs/${orgId}/sso/breakglass/${userId}`);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't remove that admin.");
    }
  }

  const addable = members.filter((m) => !admins.some((a) => a.user_id === m.user.id));

  return (
    <SettingsSection title={`Break-glass admins (${admins.length}/3)`} description="They can still sign in with a password when SSO is required.">
      {error ? <Notice tone="error">{error}</Notice> : null}
      {admins.length > 0 ? (
        <Card size="flush" className="admin-table-card">
        <table className="admin-table">
          <tbody>
            {admins.map((a) => (
              <tr key={a.user_id}>
                <td>
                  <div className="admin-row-name">{a.name || a.email}</div>
                  <div className="admin-row-sub">{a.email}</div>
                </td>
                <td>
                  <div className="row-actions">
                    <IconButton icon="trash" label="Remove" size="sm" variant="muted" onClick={() => onRemove(a.user_id)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </Card>
      ) : (
        <div className="admin-row-sub">None yet — if your IdP has an outage, nobody can sign in until it recovers.</div>
      )}
      {admins.length < 3 && addable.length > 0 ? (
        <div className="admin-inline-form">
          <Select
            className="admin-field--grow"
            aria-label="Break-glass admin to add"
            placeholder="Add a member…"
            value={addUserId}
            onChange={setAddUserId}
            options={addable.map((m) => ({ value: m.user.id, label: m.user.name || m.user.email }))}
          />
          <Button variant="secondary" onClick={onAdd} loading={adding} disabled={!addUserId}>
            Add
          </Button>
        </div>
      ) : null}
    </SettingsSection>
  );
}

function TestConnectionSection({
  orgId,
  testState,
  onClearState,
}: {
  orgId: string;
  testState: string | null;
  onClearState: () => void;
}) {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TestResultOut["result"]>(null);
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!testState) return;
    stopPolling();
    pollRef.current = window.setInterval(() => {
      api<TestResultOut>("GET", `/orgs/${orgId}/sso/test/${encodeURIComponent(testState)}`)
        .then((res) => {
          if (res.ready) {
            setResult(res.result);
            stopPolling();
          }
        })
        .catch(() => {
          setError("Couldn't fetch the test result.");
          stopPolling();
        });
    }, 1200);
    return stopPolling;
  }, [testState, orgId, stopPolling]);

  async function onStart() {
    setStarting(true);
    setError(null);
    setResult(null);
    onClearState();
    try {
      const res = await api<StartTestResp>("POST", `/orgs/${orgId}/sso/test`);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't start a test login.");
    } finally {
      setStarting(false);
    }
  }

  return (
    <SettingsSection
      title="Test connection"
      description="Opens your IdP's login in a new tab. Nothing is provisioned — this only shows the claims your IdP sends back and what Grackle would do with them."
    >
      <div>
        <Button variant="secondary" onClick={onStart} loading={starting}>
          Run a test login
        </Button>
      </div>

      {error ? <Notice tone="error">{error}</Notice> : null}
      {testState && !result ? <div className="admin-row-sub">Waiting for the test login to complete…</div> : null}

      {result ? (
        result.ok ? (
          <div className="admin-stack">
            <div className="admin-row-sub">
              Mapped: email <strong>{result.mapped?.email ?? "—"}</strong>, name{" "}
              <strong>{result.mapped?.name ?? "—"}</strong>, role <strong>{result.mapped?.role}</strong>
              {result.mapped?.groups?.length ? <>, groups <strong>{result.mapped.groups.join(", ")}</strong></> : null}
            </div>
            <pre className="admin-claims-pre">{JSON.stringify(result.claims, null, 2)}</pre>
          </div>
        ) : (
          <Notice tone="error">
            {result.error}
            {result.error_description ? `: ${result.error_description}` : ""}
          </Notice>
        )
      ) : null}
    </SettingsSection>
  );
}

// F13c-1 / SD42: what domain-scoped force-SSO covers. Read-only here; domains are added and
// verified in Org settings (DomainsSection, FR-O3).
function DomainEnforcementSection({
  orgId,
  orgName,
  enforced,
  savedEnforced,
}: {
  orgId: string;
  orgName: string;
  enforced: boolean;
  savedEnforced: boolean;
}) {
  const [domains, setDomains] = useState<OrgDomainOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<ListResponse<OrgDomainOut>>("GET", `/orgs/${orgId}/domains`)
      .then((res) => setDomains(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load domains."));
  }, [orgId]);

  const verified = domains ? verifiedDomains(domains) : [];
  const pending = domains ? domains.filter((d) => !d.verified_at).length : 0;

  return (
    <div className="admin-stack">
      <h4 className="admin-subhead">Domain enforcement</h4>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {!domains && !error ? <div className="admin-row-sub">Loading domains…</div> : null}
      {domains ? (
        verified.length === 0 ? (
          <div className="admin-row-sub">
            No verified domains. Requiring SSO covers {orgName}&apos;s current members only. Verify a
            domain in Org settings to also route new sign-ups at that domain through SSO.
          </div>
        ) : (
          <>
            <div className="admin-row-sub">
              {savedEnforced
                ? "SSO is required for every address at these domains, including people who aren't members yet (they join through SSO):"
                : enforced
                  ? "Once you save, SSO will be required for every address at these domains, including people who aren't members yet:"
                  : "When SSO is required, every address at these domains will have to sign in through SSO, including people who aren't members yet:"}
            </div>
            <div className="list">
              {verified.map((d) => (
                <div className="list-row" key={d}>
                  <div className="list-row__body">
                    <div className="list-row__title">
                      {d}
                      <Badge size="sm" tone={savedEnforced ? "success" : "neutral"}>
                        {savedEnforced ? "SSO required" : "Not enforced"}
                      </Badge>
                    </div>
                    <div className="list-row__sub">
                      Exact match: @{d} only. Subdomains such as @team.{d} need their own verified domain.
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )
      ) : null}
      {pending > 0 ? (
        <div className="admin-row-sub">
          {pending} pending domain{pending === 1 ? "" : "s"} not enforced until verified.
        </div>
      ) : null}
    </div>
  );
}
